// src/core/mvcs/view/util/FireGenerator.ts
import { Graphics, Texture, type Renderer } from "pixi.js";
import { GameConfig } from "../../../config/GameConfig";

export class FlameGenerator {
    private readonly cfg = GameConfig.FLAME;

    /**
     * Generates a procedurally generated flame texture, using multiple layers to create a soft glow effect.
     * The generated texture is a 64x64 pixel image with a transparent background.
     * The flame texture is drawn using two layers:
     * - An outer soft glow (large, very transparent)
     * - A "core" teardrop shape with a narrower top (created using Bezier curves)
     * The result is a sharp, high-quality flame texture suitable for use in particle systems.
     * 
     * @param renderer - The active WebGL/WebGPU renderer used for texture baking.
     * 
     * @returns A procedurally generated flame texture.
     */
    public generateFlameTexture(renderer: Renderer): Texture {
        const graphics = new Graphics();
        const size = this.cfg.TEXTURE_SIZE;

        // We draw multiple layers to create a "soft" feel without external assets
        // Layer 1: The outer soft glow (large, very transparent)
        graphics.circle(size * 0.5, size * 0.5, size * 0.5)
            .fill({ color: 'white', alpha: 0.2 });

        // Layer 2: The "Core" teardrop shape
        // We use a bit of math to make it narrower at the top
        graphics.moveTo(size * 0.5, size * 0.2) // Top tip
            .bezierCurveTo(
                size * 0.8, size * 0.4, // Right curve
                size * 0.9, size * 0.9, // Bottom right
                size * 0.5, size * 0.95   // Bottom center
            )
            .bezierCurveTo(
                size * 0.1, size * 0.9, // Bottom left
                size * 0.2, size * 0.4, // Left curve
                size * 0.5, size * 0.2    // Back to tip
            )
            .fill({ color: 'white', alpha: 0.8 });

        const texture = renderer.generateTexture({
            target: graphics,
            resolution: this.cfg.TEXTURE_RESOLUTION, // Keep it sharp
            antialias: true
        });

        graphics.destroy();
        return texture;
    }
}